import { Directive, ElementRef, AfterViewInit, OnDestroy, inject, output, viewChild, model, input, linkedSignal, viewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Platform } from '@ionic/angular';

import { Events } from '@providers/common/events.service';
import { UserProvider } from '@providers/user/user.service';
import { DashboardsService } from '@providers/dashboards/dashboards.service';
import { AppStorageProvider } from '@providers/common/app-storage.service';
import { CapacitorUtilsService } from '@providers/common/capacitorUtils.service';
import { DataService } from '@providers/common/data.service';

/**
 * Logica compartida entre MenuLatComponent (desktop) y MobileMenuComponent.
 * Cada subclase conserva su propio @Component (template/scss propios) y
 * sobrescribe los hooks protegidos donde su comportamiento difiere.
 */
@Directive()
export abstract class MenuBaseComponent implements AfterViewInit, OnDestroy {
    protected router = inject(Router);
    protected events = inject(Events);
    protected dashboardsService = inject(DashboardsService);
    protected storage = inject(AppStorageProvider);
    protected capacitorService = inject(CapacitorUtilsService);
    protected dataService = inject(DataService);
    protected userProvider = inject(UserProvider);
    protected plt = inject(Platform);

    /** Suscripciones propias a Events; se cancelan en ngOnDestroy. */
    protected readonly subs: Subscription[] = [];
    private destroyed = false;

    readonly sendNav = output<any>();
    readonly activeMenuItem = model('');

    readonly civarMenu = input<any[]>([]);
    readonly quantumCivarMenu = model<any[]>([]);
    public loaddedQuantumCivarMenu = false;

    //menu configurable
    showTrackingMap = false;
    showTravelList = false;
    showVesselTravelList = false;
    showScore = false;
    showClaims = false;
    showConfig = true;
    showPermissions = false;
    showRateTrip = false;
    showReport = false;
    showSingleQueryVin = true;
    showHome = true;
    showMultipleVinConsult = true;
    showConsultBySiteMexico = false;
    showExchange = false;
    showShipmentRequest = false;
    showMatrizEstatusEquipo = false;
    showAssetsManager = false;
    showProgramacionCarga = false;
    showConsultaDeTractor = false;
    showAlertasPage = false;
    showInvestorRelations = false;
    showDashboard = false;

    showDrive = false;
    showFleet = false;
    showOntrack = true;
    showYard = false;

    userFavHomePage = 0;
    currentCivarQuantumItem = 0;

    showQuantumMenu = false;
    // Submenus Civar abiertos/cerrados; se reinicia (todos cerrados) cuando cambia civarMenu.
    // Se modifica en sitio (showCivarMenu()[i] = ...), igual que antes
    public readonly showCivarMenu = linkedSignal(() => Array((this.civarMenu() ?? []).length).fill(false));
    readonly submenuElem = viewChild<ElementRef>('submenuElem');
    readonly civarElems = viewChildren<ElementRef>('civarElem');

    showHomeSubmenu = true;
    showDashboardBSCSubmenu = true;
    favouriteDashboard = '';
    readonly dashboardBSCSubmenuElem = viewChild<ElementRef>('dashboardBSCSubmenuElem');
    dashboardFinancieroActive = false;
    dashboardOperativoActive = false;
    dashboardAMDF = false;
    dashboardMazda = false;
    dashboardBMW = false;
    multipleDashboardActive = false;
    multipleDashboardBSCActive = false;
    dashboardBSC_CPVActive = false;
    dashboardBSC_CSIActive = false;
    dashboardBSC_CSIDActive = false;
    dashboardBSC_CSIXActive = false;
    dashboardBSC_SunusActive = false;
    showDashboardBSC = false;
    currentDashboard = '';
    currentDashboardBSC = '';
    currentDashboardTemp = '';
    currentDashboardBSCTemp = '';

    /* Variables para el listado de pantallas ocultas por el usuario */
    showMoreMenu = false;
    showMoreMenuDetails = true;
    readonly moreMenuSubmenuElem = viewChild<ElementRef>('moreMenuSubmenuElem');

    showTrackingMap_Collapsible = false;
    showTravelList_Collapsible = false;
    showScore_Collapsible = false;
    showConsultBySiteMexico_Collapsible = false;
    showReport_Collapsible = false;
    showExchange_Collapsible = false;
    showMultipleVinConsult_Collapsible = false;
    showMatrizEstatusEquipo_Collapsible = false;
    showProgramacionCarga_Collapsible = false;
    showAlertasPage_Collapsible = false;
    showShipmentRequest_Collapsible = false;
    showConsultaDeTractor_Collapsible = false;
    showInvestorRelations_Collapsible = false;
    showAssetsManager_Collapsible = false;
    dashboardBSC_CSIActive_Collapsible = false;
    dashboardBSC_SunusActive_Collapsible = false;
    dashboardBSC_CPVActive_Collapsible = false;
    dashboardBSC_CSIDActive_Collapsible = false;
    dashboardBSC_CSIXActive_Collapsible = false;
    showRateTrip_Collapsible = false;
    showPermissions_Collapsible = false;

    constructor() {
        this.subs.push(this.events.subscribe('nav:active', (activeMenuItem: string) => {
            this.activeMenuItem.set(activeMenuItem);
        }));

        this.subs.push(this.events.subscribe(
            'bscDashboardPage:name',
            (bscMenuActivated: string) => {
                this.currentDashboardBSC = bscMenuActivated;
            },
        ));

        this.subs.push(this.events.subscribe(
            'dashboardPage:name',
            (dashboardMenuActivated: string) => {
                this.currentDashboard = dashboardMenuActivated;
            },
        ));

        this.subs.push(this.events.subscribe('menu:reload', () => {
            this.reloadMenu();
        }));
    }

    ngAfterViewInit(): void {
        this.subs.push(this.events.subscribe('menu:dashboards:current', (dashboard: string) => {
            this.currentDashboard = dashboard;
        }));
    }

    ngOnDestroy(): void {
        // Solo las suscripciones propias: events.destroy() quitaba tambien las del otro menu
        // (menu-lat y mobile-menu comparten estos topicos)
        this.destroyed = true;
        this.subs.forEach((sub) => sub.unsubscribe());
    }

    /** Hook: por defecto el usuario se considera autenticado (caso menu-lat). */
    protected isAuthenticated(): boolean {
        return true;
    }

    /** Hook: ajustes adicionales cuando el perfil de usuario es restringido. */
    protected onRestrictedProfile(): void {
        // Punto de extension: lo sobrescriben menu-lat y/o mobile-menu
    }

    /** Hook: acciones a ejecutar una vez procesada la data del menu. */
    protected onMenuDataProcessed(_data: any): void {
        // Punto de extension: lo sobrescriben menu-lat y/o mobile-menu
    }

    /** Hook: refresco del scrollbar tras un cambio de altura del menu. */
    protected afterMenuTransition(): void {
        // Punto de extension: lo sobrescriben menu-lat y/o mobile-menu
    }

    /** Hook: vuelve a cargar las opciones del menu (disparado por el evento 'menu:reload'). */
    protected reloadMenu(): void {
        // Punto de extension: lo sobrescriben menu-lat y/o mobile-menu
    }

    /**
     * Aplica la data de preferencias/dashboards del usuario sobre las banderas
     * show*. Es el nucleo compartido entre MenuLatComponent y MobileMenuComponent;
     * cada uno decide de donde obtiene `data` y cuando se llama.
     */
    protected applyMenuPreferences(data: any): void {
        if (data.UserProfile != 1 && data.UserProfile && this.isAuthenticated()) {
            this.showConsultBySiteMexico = true;
            this.showConfig = true;
            this.showHome = false;
            this.onRestrictedProfile();
        } else if (data.Preferencias) {
            this.showHome = true;
            for (const item of data['Preferencias']) {
                switch (item.Id_Preferencia) {
                    case '1':
                        this.showTrackingMap = item.Bit_Collapsible ? true : false;
                        this.showTrackingMap_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 2
                            : this.userFavHomePage;
                        break;
                    case '2':
                        //notifications center
                        break;
                    case '3':
                        this.showTravelList = this.showVesselTravelList =
                            item.Bit_Collapsible ? true : false;
                        this.showTravelList_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 3
                            : this.userFavHomePage;
                        break;
                    case '4':
                        this.showScore = item.Bit_Collapsible ? true : false;
                        this.showScore_Collapsible = item.Bit_Collapsible ? false : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 4
                            : this.userFavHomePage;
                        break;
                    case '5':
                        this.showClaims = true;
                        this.userFavHomePage = item.Bit_Default
                            ? 5
                            : this.userFavHomePage;
                        break;
                    case '6':
                        this.showConfig = true;
                        break;
                    case '7':
                        //help
                        break;
                    case '8':
                        this.showSingleQueryVin = true;
                        this.userFavHomePage = item.Bit_Default
                            ? 8
                            : this.userFavHomePage;
                        break;
                    case '9':
                        this.showConsultBySiteMexico = item.Bit_Collapsible
                            ? true
                            : false;
                        this.showConsultBySiteMexico_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 9
                            : this.userFavHomePage;
                        break;
                    case '10':
                        this.showReport = item.Bit_Collapsible ? true : false;
                        this.showReport_Collapsible = item.Bit_Collapsible ? false : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 10
                            : this.userFavHomePage;
                        break;
                    case '11':
                        this.showExchange = item.Bit_Collapsible ? true : false;
                        this.showExchange_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 11
                            : this.userFavHomePage;
                        break;
                    case '12':
                        this.showMultipleVinConsult = item.Bit_Collapsible ? true : false;
                        this.showMultipleVinConsult_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 12
                            : this.userFavHomePage;
                        break;
                    case '15':
                        this.showMatrizEstatusEquipo = item.Bit_Collapsible
                            ? true
                            : false;
                        this.showMatrizEstatusEquipo_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 15
                            : this.userFavHomePage;
                        break;
                    case '16':
                        this.showProgramacionCarga = item.Bit_Collapsible ? true : false;
                        this.showProgramacionCarga_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 16
                            : this.userFavHomePage;
                        break;
                    case '17':
                        this.showAlertasPage = item.Bit_Collapsible ? true : false;
                        this.showAlertasPage_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 17
                            : this.userFavHomePage;
                        break;
                    case '20':
                        this.showShipmentRequest = item.Bit_Collapsible ? true : false;
                        this.showShipmentRequest_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 20
                            : this.userFavHomePage;
                        break;
                    case '21':
                        this.showConsultaDeTractor = item.Bit_Collapsible ? true : false;
                        this.showConsultaDeTractor_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 21
                            : this.userFavHomePage;
                        break;
                    case '22':
                        this.showInvestorRelations = item.Bit_Collapsible ? true : false;
                        this.showInvestorRelations_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 22
                            : this.userFavHomePage;
                        break;
                    case '23':
                        this.showAssetsManager = item.Bit_Collapsible ? true : false;
                        this.showAssetsManager_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 23
                            : this.userFavHomePage;
                        break;
                    case '24':
                        this.showDashboardBSC =
                            item.Bit_Collapsible || this.showDashboardBSC ? true : false;
                        this.dashboardBSC_CSIActive = item.Bit_Collapsible ? true : false;
                        this.currentDashboardBSCTemp = item.Bit_Default
                            ? 'csi'
                            : this.currentDashboardBSCTemp;

                        this.userFavHomePage = item.Bit_Default
                            ? -11
                            : this.userFavHomePage;
                        break;
                    case '25':
                        this.showDashboardBSC =
                            item.Bit_Collapsible || this.showDashboardBSC ? true : false;
                        this.dashboardBSC_SunusActive = item.Bit_Collapsible
                            ? true
                            : false;
                        this.currentDashboardBSCTemp = item.Bit_Default
                            ? 'sunus'
                            : this.currentDashboardBSCTemp;

                        this.userFavHomePage = item.Bit_Default
                            ? -14
                            : this.userFavHomePage;
                        break;
                    case '26':
                        this.showDashboardBSC =
                            item.Bit_Collapsible || this.showDashboardBSC ? true : false;
                        this.dashboardBSC_CPVActive = item.Bit_Collapsible ? true : false;
                        this.currentDashboardBSCTemp = item.Bit_Default
                            ? 'cpv'
                            : this.currentDashboardBSCTemp;

                        this.userFavHomePage = item.Bit_Default
                            ? -10
                            : this.userFavHomePage;
                        break;
                    case '27':
                        this.showDashboardBSC =
                            item.Bit_Collapsible || this.showDashboardBSC ? true : false;
                        this.dashboardBSC_CSIDActive = item.Bit_Collapsible
                            ? true
                            : false;
                        this.currentDashboardBSCTemp = item.Bit_Default
                            ? 'csid'
                            : this.currentDashboardBSCTemp;

                        this.userFavHomePage = item.Bit_Default
                            ? -12
                            : this.userFavHomePage;
                        break;
                    case '28':
                        this.showDashboardBSC =
                            item.Bit_Collapsible || this.showDashboardBSC ? true : false;
                        this.dashboardBSC_CSIXActive = item.Bit_Collapsible
                            ? true
                            : false;
                        this.currentDashboardBSCTemp = item.Bit_Default
                            ? 'csix'
                            : this.currentDashboardBSCTemp;

                        this.userFavHomePage = item.Bit_Default
                            ? -13
                            : this.userFavHomePage;
                        break;
                    /* case '29':
                        this.showRateTrip = item.Bit_Collapsible ? true : false;
                        this.showRateTrip_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 24
                            : this.userFavHomePage;
                        break; */
                    case '30':
                        this.showPermissions = item.Bit_Collapsible ? true : false;
                        this.showPermissions_Collapsible = item.Bit_Collapsible
                            ? false
                            : true;

                        this.userFavHomePage = item.Bit_Default
                            ? 30
                            : this.userFavHomePage;
                        break;
                    default:
                        break;
                }
            }
        }

        this.loaddedQuantumCivarMenu =
            this.quantumCivarMenu().length > 0 ? true : false;
        if (!this.loaddedQuantumCivarMenu) {
            this.onReloadQuantumCivarmenu();
        }

        const route = this.router.url;
        if (route.includes('/dashboard_bsc/')) {
            this.onRouteIsDashboarBSC(
                this.router.url.replace('/dashboard_bsc/', ''),
            );
        }

        // El siguiente bloque de codigo se encarga de mostrar en el menu los dashboards
        if (data.Dashboards) {
            console.log('Loading Dashboards...');
            for (const dashboard of data['Dashboards']) {
                switch (dashboard.Id_Dashboard) {
                    case '1': // Dashboard Financiero
                        this.dashboardFinancieroActive = dashboard.Bit_Collapsible
                            ? true
                            : false;
                        this.userFavHomePage = dashboard.Bit_Default_Home
                            ? -1
                            : this.userFavHomePage;
                        this.currentDashboardTemp = dashboard.Bit_Default_Home
                            ? 'financiero'
                            : this.currentDashboardTemp;

                        if (dashboard.Bit_Default)
                            this.currentDashboard = this.favouriteDashboard = 'financiero';
                        break;
                    case '2': // Dashboard Operativo
                        this.dashboardOperativoActive = dashboard.Bit_Collapsible
                            ? true
                            : false;
                        this.userFavHomePage = dashboard.Bit_Default_Home
                            ? -2
                            : this.userFavHomePage;
                        this.currentDashboardTemp = dashboard.Bit_Default_Home
                            ? 'operativo'
                            : this.currentDashboardTemp;

                        if (dashboard.Bit_Default)
                            this.currentDashboard = this.favouriteDashboard = 'operativo';
                        break;
                    case '3': // Dashboard AMDF
                        this.dashboardAMDF = dashboard.Bit_Collapsible ? true : false;
                        this.userFavHomePage = dashboard.Bit_Default_Home
                            ? -3
                            : this.userFavHomePage;
                        this.currentDashboardTemp = dashboard.Bit_Default_Home
                            ? 'amdf'
                            : this.currentDashboardTemp;

                        if (dashboard.Bit_Default)
                            this.currentDashboard = this.favouriteDashboard = 'amdf';
                        break;
                    case '4': // Dashboard Mazda
                        this.dashboardMazda = dashboard.Bit_Collapsible ? true : false;
                        this.userFavHomePage = dashboard.Bit_Default_Home
                            ? -4
                            : this.userFavHomePage;
                        this.currentDashboardTemp = dashboard.Bit_Default_Home
                            ? 'mazda'
                            : this.currentDashboardTemp;

                        if (dashboard.Bit_Default)
                            this.currentDashboard = this.favouriteDashboard = 'mazda';
                        break;
                    case '5': // Dashboard BMW
                        this.dashboardBMW = dashboard.Bit_Collapsible ? true : false;
                        this.userFavHomePage = dashboard.Bit_Default_Home
                            ? -5
                            : this.userFavHomePage;
                        this.currentDashboardTemp = dashboard.Bit_Default_Home
                            ? 'bmw'
                            : this.currentDashboardTemp;

                        if (dashboard.Bit_Default)
                            this.currentDashboard = this.favouriteDashboard = 'bmw';
                        break;
                    default:
                        break;
                }
            }

            this.enableHomeSubMenuShow(
                this.dashboardFinancieroActive,
                this.dashboardOperativoActive,
                this.dashboardAMDF,
                this.dashboardMazda,
                this.dashboardBMW,
            );
        }

        this.onMenuDataProcessed(data);
    }

    onReloadQuantumCivarmenu() {
        this.userProvider
            .getQuantumCivarMenu()
            .then((data: any) => {
                // console.log('Civar Menu: ', JSON.stringify(data));

                // La promesa puede resolverse despues de destruir el menu (p.ej. al cerrar sesion)
                if (this.destroyed) return;
                this.quantumCivarMenu.set(data);

                this.loaddedQuantumCivarMenu =
                    this.quantumCivarMenu().length > 0 ? true : false;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    onRouteIsDashboarBSC(currentDashboard: string) {
        this.currentDashboardBSC = currentDashboard;
    }

    enableHomeSubMenuShow(
        activeFinanciero: boolean,
        activeOperativo: boolean,
        activeAMDF: boolean,
        activeMazda: boolean,
        activeBMW: boolean,
    ) {
        const arrayDashboard = [
            { id: 1, bool: activeFinanciero },
            { id: 2, bool: activeOperativo },
            { id: 3, bool: activeAMDF },
            { id: 4, bool: activeMazda },
            { id: 5, bool: activeBMW },
        ];

        let activeDashboard = 0;

        arrayDashboard.forEach((dashboardItem) => {
            if (this.check(dashboardItem)) {
                activeDashboard++;
            }
        });

        if (arrayDashboard.every(this.check)) {
            this.multipleDashboardActive = true;
            this.showDashboard = true;
        } else {
            if (activeDashboard > 1 && activeDashboard <= 4) {
                this.multipleDashboardActive = true;
                this.showDashboard = true;
            } else {
                if (activeDashboard === 1) {
                    this.multipleDashboardActive = false;
                    this.showDashboard = true;
                } else {
                    this.showDashboard = false;
                }
            }
        }
    }

    check(item: any) {
        return item.bool === true || false;
    }

    toggleMenu(isCivar: boolean, idx = 0) {
        if (isCivar) {
            const otherElems = this.civarElems().filter((el, i) => {
                if (i !== idx) this.showCivarMenu()[i] = false;
                return i !== idx;
            });
            otherElems.forEach((el) => {
                el.nativeElement.style.maxHeight = '0px';
            });
            this.showCivarMenu()[idx] = !this.showCivarMenu()[idx];
            const elem = this.civarElems().find((el, i) => i === idx);
            elem.nativeElement.style.maxHeight = this.showCivarMenu()[idx]
                ? elem.nativeElement.scrollHeight + 'px'
                : '0px';
            if (!this.showCivarMenu()[idx]) this.events.publish('menues:close', 1);
        } else {
            for (let index = 0; index < this.showCivarMenu().length; index++) {
                this.showCivarMenu()[index] = false;
            }
            this.events.publish('menues:close', 1);

            this.civarElems().forEach((el) => {
                el.nativeElement.style.maxHeight = '0px';
            });

            this.showQuantumMenu = !this.showQuantumMenu;
            const submenuElem = this.submenuElem();
            if (submenuElem && submenuElem.nativeElement) {
                submenuElem.nativeElement.style.maxHeight = this.showQuantumMenu
                    ? submenuElem.nativeElement.scrollHeight + 'px'
                    : '0px';
            }
        }

        this.afterMenuTransition();
    }

    resizeContainer(ev: any, idx = 0) {
        const elem = this.civarElems().find((el, i) => idx === i);
        elem.nativeElement.style.maxHeight =
            elem.nativeElement.scrollHeight + ev + 'px';
        this.afterMenuTransition();
    }

    toggleDashboardBSCSubmenu() {
        this.showDashboardBSCSubmenu = !this.showDashboardBSCSubmenu;
        const dashboardBSCSubmenuElem = this.dashboardBSCSubmenuElem();
        dashboardBSCSubmenuElem.nativeElement.style.maxHeight = this
            .showDashboardBSCSubmenu
            ? dashboardBSCSubmenuElem.nativeElement.scrollHeight + 'px'
            : '0px';
        this.submenuElem().nativeElement.style.maxHeight =
            this.submenuElem().nativeElement.scrollHeight +
            dashboardBSCSubmenuElem.nativeElement.style.maxHeight +
            'px';
        this.afterMenuTransition();
    }

    toggleShowMoreSubmenu() {
        this.showMoreMenuDetails = !this.showMoreMenuDetails;
        const moreMenuSubmenuElem = this.moreMenuSubmenuElem();
        moreMenuSubmenuElem.nativeElement.style.maxHeight = this
            .showMoreMenuDetails
            ? moreMenuSubmenuElem.nativeElement.scrollHeight + 'px'
            : '0px';
        this.submenuElem().nativeElement.style.maxHeight =
            this.submenuElem().nativeElement.scrollHeight +
            moreMenuSubmenuElem.nativeElement.style.maxHeight +
            'px';
        this.afterMenuTransition();
    }

    async selectFavouriteDashboard(dashboard: string) {
        (await this.dashboardsService.setDashboardFavorito(dashboard)).subscribe({
            next: () => {
                this.favouriteDashboard = dashboard;
            },
            error: (err) => {
                console.error(err);
            },
        });
    }

    goToDashboardFinanciero() {
        this.currentDashboard = 'financiero';
        this.events.publish('menu:dashboards:current', this.currentDashboard);

        this.sendNav.emit(-1);
    }

    goToDashboardOperativo() {
        this.currentDashboard = 'operativo';
        this.events.publish('menu:dashboards:current', this.currentDashboard);

        this.sendNav.emit(-2);
    }

    goToDashboardAMDF() {
        this.currentDashboard = 'amdf';
        this.events.publish('menu:dashboards:current', this.currentDashboard);

        this.sendNav.emit(-3);
    }

    goToDashboardMazda() {
        this.currentDashboard = 'mazda';
        this.events.publish('menu:dashboards:current', this.currentDashboard);

        this.sendNav.emit(-4);
    }

    goToDashboardBMW() {
        this.currentDashboard = 'bmw';
        this.events.publish('menu:dashboards:current', this.currentDashboard);

        this.sendNav.emit(-5);
    }

    goToDashboardBSCCPV() {
        this.currentDashboardBSC = 'cpv';
        this.sendNav.emit(-10);
    }

    goToDashboardBSCCSI() {
        this.currentDashboardBSC = 'csi';
        this.sendNav.emit(-11);
    }

    goToDashboardBSCCSID() {
        this.currentDashboardBSC = 'csid';
        this.sendNav.emit(-12);
    }

    goToDashboardBSCCSIX() {
        this.currentDashboardBSC = 'csix';
        this.sendNav.emit(-13);
    }

    goToDashboardBSCSunus() {
        this.currentDashboardBSC = 'sunus';
        this.sendNav.emit(-14);
    }

    goToHome() {
        if (this.userFavHomePage !== 0) {
            if (
                this.userFavHomePage === -1 ||
                this.userFavHomePage === -2 ||
                this.userFavHomePage === -3 ||
                this.userFavHomePage === -4 ||
                this.userFavHomePage === -5
            ) {
                this.currentDashboard = this.currentDashboardTemp;
            } else if (
                this.userFavHomePage === -10 ||
                this.userFavHomePage === -11 ||
                this.userFavHomePage === -12 ||
                this.userFavHomePage === -13 ||
                this.userFavHomePage === -14
            ) {
                this.currentDashboardBSC = this.currentDashboardBSCTemp;
            }

            this.sendNav.emit(this.userFavHomePage);
        } else {
            this.sendNav.emit(1);
        }
    }

    goToClaims() {
        this.sendNav.emit(5);
    }

    goToUserPermissions() {
        this.sendNav.emit(60);
    }

    goToConfig() {
        this.sendNav.emit(6);
    }

    goToSingleQueryVin() {
        this.sendNav.emit(8);
    }

    goToTravelList() {
        this.sendNav.emit(3);
    }

    goToVesselTravelList() {
        this.sendNav.emit(25);
    }

    goToTrackingMap() {
        this.sendNav.emit(2);
    }

    goToScore() {
        this.sendNav.emit(4);
    }

    goToReports() {
        this.sendNav.emit(10);
    }

    goToExchange() {
        this.sendNav.emit(11);
    }

    goToShipmentRequest() {
        this.sendNav.emit(20);
    }

    goToMultipleVinConsult() {
        this.sendNav.emit(12);
    }

    goToConsultBySiteMexico() {
        this.sendNav.emit(9);
    }

    goToMatrizEstatusEquipo() {
        this.sendNav.emit(15);
    }

    goToProgramacionCarga() {
        this.sendNav.emit(16);
    }

    goToAlertasPage() {
        this.sendNav.emit(17);
    }

    goToConsultaDeTractor() {
        this.sendNav.emit(21);
    }

    goToInvestorRelationsPage() {
        this.sendNav.emit(22);
    }

    goToAssetsManagerPage() {
        this.sendNav.emit(23);
    }

    goToRateTrip() {
        this.sendNav.emit(24);
    }

    goToWebFrame() {
        this.sendNav.emit(100);
    }

    logout() {
        this.sendNav.emit(14);
    }

    getIconNameCivarMenu(nameCivar: string) {
        let nameIcon = 'earth';

        switch (nameCivar) {
            case 'ADMINISTRACION':
                nameIcon = 'person-circle-outline';
                break;
            case 'OPERACION':
                nameIcon = 'earth';
                break;
            case 'CALIDAD':
                nameIcon = 'medal-outline';
                break;
            case 'RH':
                nameIcon = 'people-outline';
                break;
            case 'TI':
                nameIcon = 'server-outline';
                break;
            case 'ASSET MANAGER':
                nameIcon = 'layers-outline';
        }

        return nameIcon;
    }

    /**
     * @name openPage
     * @description Metodo que abre una pagina del menu
     */
    openPage(item: any) {
        this.currentCivarQuantumItem = item.Id_Cnsc_Menu;
        this.events.publish('nav:active', 'civar-item');

        if (item.Desc_Pagina) {
            if (item.Desc_Pagina.includes('/ARTUS')) {
                // Reservado para el flujo de auto-login a ARTUS (ver MenuLatComponent.autoLoginARTUS)
            } else {
                if (
                    this.plt.is('mobile') &&
                    (this.plt.is('android') || this.plt.is('ios'))
                ) {
                    this.capacitorService.goToLaunchBrowser(item.Desc_Pagina);
                } else {
                    if (item.Bit_Frame) {
                        const routeParams = {
                            externalURL: item.Desc_Pagina,
                            routeId: item.Id_Cnsc_Menu,
                        };
                        this.dataService.setData(routeParams.routeId, routeParams);
                        this.storage.put(routeParams.routeId, routeParams);

                        this.router.navigate(['/quantum/civar/' + routeParams.routeId], {
                            replaceUrl: true,
                        });
                    } else {
                        window.open(item.Desc_Pagina, '_blank');
                    }
                }
            }
        }
    }
}
